import { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { QrCode, Download, ExternalLink, Save } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LANGS = [
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
];

export default function MenuI18n() {
  const { can } = useAuth();
  // FASE 7.4 STEP F.1 — menu translations gate on menu.translate.
  const canTranslate = can("menu.translate");
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({}); // id -> translations map
  const [urls, setUrls] = useState({});

  const load = async () => {
    const { data } = await api.get("/menu");
    setItems(data);
    const d = {};
    data.forEach((m) => { d[m.id] = m.translations || { it: { name: m.name, description: m.description || "" }, en: {}, de: {} }; });
    setDrafts(d);
    const urls = {};
    for (const l of LANGS) {
      const r = await api.get(`/menu/qr-url?lang=${l.code}`);
      urls[l.code] = r.data.url;
    }
    setUrls(urls);
  };
  useEffect(() => { load(); }, []);

  const setField = (id, lang, field, value) => {
    setDrafts((d) => ({ ...d, [id]: { ...d[id], [lang]: { ...(d[id]?.[lang] || {}), [field]: value } } }));
  };

  const save = async (id) => {
    try { await api.put(`/menu/${id}/translations`, { translations: drafts[id] }); toast.success("Traduzioni salvate"); }
    catch { toast.error("Errore salvataggio"); }
  };

  return (
    <div data-testid="menu-i18n-page">
      <PageHeader eyebrow="Menu digitale" title="QR & traduzioni"
        subtitle="Menu multilingua (IT/EN/DE) accessibile ai clienti tramite QR code. Ideale per il turismo di Orosei." />

      {/* QR codes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        {LANGS.map((l) => (
          <div key={l.code} className="border border-[#E2D9C8] rounded-lg p-6 bg-white text-center" data-testid={`qr-${l.code}`}>
            <div className="text-4xl mb-3">{l.flag}</div>
            <div className="label-eyebrow">Menu {l.label}</div>
            <div className="mt-4 flex justify-center">
              <img src={`${API_BASE}/menu/qr?lang=${l.code}`} alt={`QR ${l.code}`} className="w-48 h-48 border border-[#E2D9C8] rounded-md bg-[#FDFBF7]" />
            </div>
            <div className="mt-4 text-xs text-neutral-500 break-all font-mono">{urls[l.code]}</div>
            <div className="mt-4 flex gap-2 justify-center">
              <a href={`${API_BASE}/menu/qr?lang=${l.code}`} download={`carasau-menu-${l.code}.png`}
                className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-neutral-900 rounded hover:bg-neutral-900 hover:text-[#FDFBF7] transition-colors">
                <Download size={12} strokeWidth={1.5} /> Scarica PNG
              </a>
              <a href={urls[l.code]} target="_blank" rel="noreferrer"
                className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-[#E2D9C8] rounded hover:border-neutral-900 transition-colors">
                <ExternalLink size={12} strokeWidth={1.5} /> Apri
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* Translations editor */}
      <h2 className="font-serif text-2xl mb-3">Traduzioni menu</h2>
      <div className="space-y-4">
        {items.map((m) => (
          <div key={m.id} className="border border-[#E2D9C8] rounded-lg bg-white p-5" data-testid={`i18n-${m.id}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="label-eyebrow capitalize">{m.category}</div>
                <div className="font-serif text-xl">{m.name}</div>
                <div className="text-xs text-neutral-500">€ {m.sale_price?.toFixed(2)}</div>
              </div>
              {canTranslate && (
                <Button size="sm" onClick={() => save(m.id)} data-testid={`save-i18n-${m.id}`} className="bg-neutral-900 text-[#FDFBF7]"><Save size={12} strokeWidth={1.5} /> Salva</Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {LANGS.map((l) => (
                <div key={l.code} className="border border-[#E2D9C8] rounded p-3 bg-[#FDFBF7]">
                  <div className="label-eyebrow mb-2 flex items-center gap-1">{l.flag} {l.label}</div>
                  <Input data-testid={`name-${m.id}-${l.code}`} placeholder="Nome" value={drafts[m.id]?.[l.code]?.name || ""}
                    onChange={(e) => setField(m.id, l.code, "name", e.target.value)} className="mb-2 bg-white" disabled={!canTranslate} />
                  <Textarea data-testid={`desc-${m.id}-${l.code}`} placeholder="Descrizione" rows={2}
                    value={drafts[m.id]?.[l.code]?.description || ""}
                    onChange={(e) => setField(m.id, l.code, "description", e.target.value)} className="bg-white text-sm" disabled={!canTranslate} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
